<template>
  <div v-loading="loading" class="nx-page nx-report-page wagon-efficiency-daily">
    <nx-report-header
      title="翻车机效率日报"
      subtitle="按翻车作业计划、车辆作业进度和日统计口径汇总当日效率与周趋势"
    >
      <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新数据</el-button>
    </nx-report-header>

    <el-card shadow="never" class="filter-card">
      <el-form inline :model="query" size="small" @submit.native.prevent>
        <el-form-item label="统计日期">
          <el-date-picker
            v-model="query.statDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
            :clearable="false"
          />
        </el-form-item>
        <el-form-item label="设备">
          <el-input v-model.trim="query.deviceCode" clearable maxlength="40" placeholder="全部翻车机" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="loadData">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-alert
      v-if="loadError"
      class="state-alert"
      type="error"
      :title="loadError"
      show-icon
      :closable="false"
    />

    <div class="kpi-grid">
      <el-card v-for="item in kpis" :key="item.label" shadow="hover" class="kpi-card">
        <div class="kpi-label">{{ item.label }}</div>
        <div class="kpi-value" :class="item.className">{{ item.value }}</div>
        <div class="kpi-sub">{{ item.sub }}</div>
      </el-card>
    </div>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="16">
        <el-card shadow="hover" class="block-card">
          <div class="block-head">
            <span class="block-title">本周 vs 上周效率趋势</span>
            <el-tag size="mini" type="info">自然周对比</el-tag>
          </div>
          <div v-show="hasTrendData" ref="trendChart" class="chart-box" />
          <el-empty v-show="!hasTrendData" description="暂无趋势数据" :image-size="80" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card shadow="hover" class="block-card compare-card">
          <div class="block-head">
            <span class="block-title">趋势对比</span>
          </div>
          <div class="compare-list">
            <div class="compare-item">
              <span class="compare-label">本周平均利用率</span>
              <strong>{{ percentText(weekCompare.currentUtilizationRate) }}</strong>
              <small>{{ weekRangeText('current') }}</small>
            </div>
            <div class="compare-item">
              <span class="compare-label">上周平均利用率</span>
              <strong>{{ percentText(weekCompare.lastUtilizationRate) }}</strong>
              <small>{{ weekRangeText('last') }}</small>
            </div>
            <div class="compare-item">
              <span class="compare-label">效率变化</span>
              <strong :class="directionClass">{{ changeText }}</strong>
              <small>较上周</small>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="report-row">
      <el-col v-for="item in reportCards" :key="item.title" :xs="24" :md="8">
        <el-card shadow="never" class="report-card">
          <div class="report-title">{{ item.title }}</div>
          <div class="report-text">{{ item.text || '-' }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="block-card">
      <div class="block-head">
        <span class="block-title">效率明细</span>
        <el-tag size="mini">只读</el-tag>
      </div>
      <el-table :data="detailRows" border stripe style="width: 100%" empty-text="暂无统计数据">
        <el-table-column prop="statDate" label="统计日期" width="120" />
        <el-table-column prop="deviceCode" label="设备" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.deviceCode || '全部翻车机' }}</template>
        </el-table-column>
        <el-table-column prop="operationCount" label="翻卸次数" width="100" align="right" />
        <el-table-column prop="vehicleCount" label="完成车厢" width="100" align="right" />
        <el-table-column prop="totalOperationSeconds" label="总作业时长" width="130" align="right">
          <template slot-scope="{ row }">{{ secondsText(row.totalOperationSeconds) }}</template>
        </el-table-column>
        <el-table-column prop="plannedSeconds" label="计划作业时长" width="130" align="right">
          <template slot-scope="{ row }">{{ secondsText(row.plannedSeconds) }}</template>
        </el-table-column>
        <el-table-column prop="avgOperationSeconds" label="平均单次时长" width="130" align="right">
          <template slot-scope="{ row }">{{ minutesText(row.avgOperationSeconds) }}</template>
        </el-table-column>
        <el-table-column prop="utilizationRate" label="利用率" width="110" align="right">
          <template slot-scope="{ row }">{{ percentText(row.utilizationRate) }}</template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchEfficiencyDashboard } from './api'

export default {
  name: 'WagonTipplerEfficiencyDaily',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      loadError: '',
      query: {
        statDate: this.todayText(),
        deviceCode: ''
      },
      summary: {},
      weekCompare: {},
      trend: [],
      report: {},
      trendChart: null
    }
  },
  computed: {
    hasTrendData() {
      return this.trend.some((item) => item && (item.utilizationRate != null || item.operationCount != null))
    },
    kpis() {
      return [
        {
          label: '当日翻卸次数',
          value: this.intText(this.summary.operationCount),
          sub: `完成车厢 ${this.intText(this.summary.vehicleCount)} 节`,
          className: 'blue'
        },
        {
          label: '总作业时长',
          value: this.secondsText(this.summary.totalOperationSeconds),
          sub: '实际作业时长汇总',
          className: ''
        },
        {
          label: '平均单次作业时长',
          value: this.minutesText(this.summary.avgOperationSeconds),
          sub: '总作业时长 / 翻卸次数',
          className: 'orange'
        },
        {
          label: '设备利用率',
          value: this.percentText(this.summary.utilizationRate),
          sub: '实际作业时长 / 计划作业时长',
          className: 'green'
        }
      ]
    },
    reportCards() {
      return [
        { title: '趋势结论', text: this.report.summaryText },
        { title: '风险提示', text: this.report.riskText },
        { title: '建议', text: this.report.suggestionText }
      ]
    },
    detailRows() {
      const row = {
        statDate: this.summary.statDate || this.query.statDate,
        deviceCode: this.summary.deviceCode || this.query.deviceCode,
        operationCount: this.numberValue(this.summary.operationCount, 0),
        vehicleCount: this.numberValue(this.summary.vehicleCount, 0),
        totalOperationSeconds: this.numberValue(this.summary.totalOperationSeconds, 0),
        plannedSeconds: this.numberValue(this.summary.plannedSeconds, 0),
        avgOperationSeconds: this.numberValue(this.summary.avgOperationSeconds, 0),
        utilizationRate: this.numberValue(this.summary.utilizationRate, 0)
      }
      return this.hasSummary(row) ? [row] : []
    },
    directionClass() {
      const direction = this.weekCompare.direction
      if (direction === 'up') return 'good'
      if (direction === 'down') return 'bad'
      const value = this.numberValue(this.weekCompare.changeValue, 0)
      return value > 0 ? 'good' : value < 0 ? 'bad' : ''
    },
    changeText() {
      const value = this.numberValue(this.weekCompare.changeValue, 0)
      const percent = this.weekCompare.changePercent
      const prefix = value > 0 ? '+' : ''
      const suffix = percent === null || percent === undefined || percent === ''
        ? ''
        : ` / ${prefix}${this.formatNumber(percent, 1)}%`
      return `${prefix}${this.formatNumber(value, 1)} pct${suffix}`
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeChart()
  },
  methods: {
    async loadData() {
      if (!this.query.statDate) {
        this.$message.warning('请选择统计日期')
        return
      }
      this.loading = true
      this.loadError = ''
      try {
        const payload = {
          statDate: this.query.statDate,
          deviceCode: this.query.deviceCode || undefined
        }
        const data = await fetchEfficiencyDashboard(payload)
        this.applyDashboard(data || {})
      } catch (e) {
        this.applyDashboard({})
        this.loadError = (e && e.message) || '加载失败'
        this.$message.error(this.loadError)
      } finally {
        this.loading = false
      }
    },
    resetQuery() {
      this.query = {
        statDate: this.todayText(),
        deviceCode: ''
      }
      this.loadData()
    },
    applyDashboard(data) {
      this.summary = data.summary || {}
      this.weekCompare = data.weekCompare || {}
      this.trend = Array.isArray(data.trend) ? data.trend : []
      this.report = data.report || {}
      this.$nextTick(() => this.renderTrendChart())
    },
    renderTrendChart() {
      if (!this.$refs.trendChart) return
      if (!this.hasTrendData) {
        this.disposeChart()
        return
      }
      if (this.trendChart) this.trendChart.dispose()
      this.trendChart = echarts.init(this.$refs.trendChart)
      this.trendChart.setOption({
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            return params.map((item) => {
              const unit = item.seriesName === '翻卸次数' ? ' 次' : '%'
              return `${item.marker}${item.seriesName}: ${item.value}${unit}`
            }).join('<br/>')
          }
        },
        legend: { data: ['设备利用率', '翻卸次数'], bottom: 0 },
        grid: { left: '3%', right: '4%', top: '10%', bottom: '16%', containLabel: true },
        xAxis: {
          type: 'category',
          data: this.trend.map((item) => item.statDate || '-'),
          axisLabel: { color: '#606266' }
        },
        yAxis: [
          { type: 'value', name: '利用率(%)', min: 0, axisLabel: { formatter: '{value}%' } },
          { type: 'value', name: '翻卸次数', min: 0 }
        ],
        series: [
          {
            name: '设备利用率',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            data: this.trend.map((item) => this.numberValue(item.utilizationRate, 0)),
            lineStyle: { color: '#409EFF', width: 3 },
            itemStyle: { color: '#409EFF' },
            areaStyle: { opacity: 0.08 }
          },
          {
            name: '翻卸次数',
            type: 'bar',
            yAxisIndex: 1,
            data: this.trend.map((item) => this.numberValue(item.operationCount, 0)),
            barMaxWidth: 28,
            itemStyle: { color: '#67C23A', borderRadius: [4, 4, 0, 0] }
          }
        ]
      }, true)
    },
    onResize() {
      this.trendChart && this.trendChart.resize()
    },
    disposeChart() {
      if (this.trendChart) {
        this.trendChart.dispose()
        this.trendChart = null
      }
    },
    todayText() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    hasSummary(row) {
      return ['operationCount', 'vehicleCount', 'totalOperationSeconds', 'plannedSeconds', 'avgOperationSeconds', 'utilizationRate']
        .some((key) => Number(row[key]) > 0)
    },
    weekRangeText(type) {
      const start = type === 'current' ? this.weekCompare.currentWeekStart : this.weekCompare.lastWeekStart
      const end = type === 'current' ? this.weekCompare.currentWeekEnd : this.weekCompare.lastWeekEnd
      return start && end ? `${start} 至 ${end}` : '-'
    },
    numberValue(value, fallback) {
      const n = Number(value)
      return isNaN(n) ? fallback : n
    },
    formatNumber(value, digits) {
      const n = Number(value)
      if (isNaN(n)) return '-'
      return n.toFixed(digits)
    },
    intText(value) {
      return String(Math.round(this.numberValue(value, 0)))
    },
    secondsText(value) {
      const seconds = this.numberValue(value, 0)
      if (!seconds) return '0 h'
      return `${(seconds / 3600).toFixed(1)} h`
    },
    minutesText(value) {
      const seconds = this.numberValue(value, 0)
      if (!seconds) return '0 min'
      return `${(seconds / 60).toFixed(1)} min`
    },
    percentText(value) {
      return `${this.formatNumber(this.numberValue(value, 0), 1)}%`
    }
  }
}
</script>

<style scoped lang="scss">
@import './index.scss';
</style>
